/**
 * SupplierLedgerDialog
 * --------------------
 * One vendor's whole payables history — purchase orders, invoices, payments —
 * with aging buckets, the remit and bank details on file (masked, with a logged
 * reveal), and a printable statement.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Banknote, Eye, FileText, Loader2, Printer, Save, ShieldCheck, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API } from "@/App";
import { openAuthedDoc } from "@/lib/openAuthedDoc";
import SupplierPayablesDesk from "@/components/admin/SupplierPayablesDesk";

const money = (v) => `$${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PROFILE_FIELDS = [
  ["payment_terms", "Payment terms", "Net 30"],
  ["preferred_method", "Preferred method", "wire / ach / check"],
  ["remit_to_name", "Remit to (name)", ""],
  ["remit_to_address", "Remit to (address)", ""],
  ["remit_email", "Remittance email", ""],
  ["currency", "Currency", "USD"],
];
const BANK_FIELDS = [
  ["bank_name", "Bank name"], ["account_name", "Account name"],
  ["routing_aba", "Routing / ABA"], ["account_number", "Account number"],
  ["account_type", "Account type"], ["swift_bic", "SWIFT / BIC"],
  ["iban", "IBAN"], ["wire_reference", "Wire reference"],
  ["ach_email", "ACH notification email"], ["check_payable_to", "Check payable to"],
  ["check_remit_address", "Check remit address"], ["portal_url", "Payment portal"],
];

export const SupplierLedgerDialog = ({ open, onClose, supplierId, supplierName, token }) => {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("invoices");
  const [profile, setProfile] = useState({});
  const [bank, setBank] = useState({});
  const [revealed, setRevealed] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!supplierId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/invoice-audit/suppliers/${supplierId}/ledger`, { headers });
      setData(r.data);
      const p = r.data?.payment_profile || {};
      setProfile({
        payment_terms: p.payment_terms || "", preferred_method: p.preferred_method || "",
        remit_to_name: p.remit_to_name || "", remit_to_address: p.remit_to_address || "",
        remit_email: p.remit_email || "", currency: p.currency || "USD",
        w9_on_file: !!p.w9_on_file, notes: p.notes || "",
      });
      setBank({});
      setRevealed(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load that supplier's ledger");
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierId, token]);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;

  const reveal = async () => {
    try {
      const r = await axios.get(`${API}/invoice-audit/suppliers/${supplierId}/payment-profile`,
        { headers, params: { reveal: true } });
      setRevealed(r.data?.profile?.bank_revealed || {});
      toast.info("Bank details revealed — this view is written to the audit log");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reveal those details");
    }
  };

  const save = async (markVerified = false) => {
    setSaving(true);
    try {
      const body = { ...profile };
      if (Object.keys(bank).length) body.bank = bank;
      if (markVerified) body.mark_verified = true;
      await axios.put(`${API}/invoice-audit/suppliers/${supplierId}/payment-profile`, body, { headers });
      toast.success(markVerified ? "Payment details saved and marked verified" : "Payment details saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const p = data?.payment_profile || {};
  const s = data?.summary || {};
  const onFile = p.bank || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-5xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()} data-testid="supplier-ledger-dialog">
        <div className="px-4 py-3 border-b border-slate-200 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-black text-slate-900">
              {data?.supplier?.name || supplierName} — payables ledger
            </h3>
            <p className="text-[11px] text-slate-500">
              {data?.supplier?.email || ""}{data?.supplier?.phone ? ` · ${data.supplier.phone}` : ""}
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => openAuthedDoc(`/invoice-audit/suppliers/${supplierId}/statement`,
              { token, fmt: "html" })}
              className="inline-flex items-center h-8 px-2.5 rounded-md border border-slate-300 text-xs hover:bg-slate-50"
              data-testid="statement-preview">
              <FileText className="w-3.5 h-3.5 mr-1" /> Statement
            </button>
            <button onClick={() => openAuthedDoc(`/invoice-audit/suppliers/${supplierId}/statement`,
              { token, fmt: "pdf" })}
              className="inline-flex items-center h-8 px-2.5 rounded-md border border-slate-300 text-xs hover:bg-slate-50"
              data-testid="statement-pdf">
              <Printer className="w-3.5 h-3.5 mr-1" /> Print PDF
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
          </div>
        </div>

        {loading ? (
          <p className="p-6 text-slate-500 inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>
        ) : (
          <div className="overflow-auto p-4">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {[["Open balance", money(s.open_balance)], ["Overdue", money(s.overdue_balance)],
                ["Billed to date", money(s.billed_total)], ["Paid to date", money(s.paid_total)],
                ["Awaiting approval", s.awaiting_approval]].map(([k, v], i) => (
                <div key={k} className={`rounded-lg border px-3 py-2 ${i === 1 && s.overdue_balance > 0
                  ? "border-red-200 bg-red-50" : "border-slate-200 bg-white"}`}>
                  <p className="text-[9px] font-black uppercase tracking-wider text-slate-500">{k}</p>
                  <p className={`text-base font-black ${i === 1 && s.overdue_balance > 0 ? "text-red-700" : "text-slate-900"}`}>{v}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2 mt-3 text-[10.5px]">
              {[["current", "Current"], ["1_30", "1-30"], ["31_60", "31-60"], ["61_90", "61-90"],
                ["over_90", "90+"], ["no_due_date", "No due date"]].map(([k, label]) => (
                <span key={k} className="px-2 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-700">
                  {label}: <strong>{money((s.aging || {})[k])}</strong>
                </span>
              ))}
            </div>

            <div className="flex gap-2 mt-4 border-b border-slate-200">
              {[["invoices", `Invoices (${(data.invoices || []).length})`],
                ["payments", `Payments (${(data.payments || []).length})`],
                ["pos", `Purchase orders (${(data.purchase_orders || []).length})`],
                ["entry", "Payables & invoices (enter / edit)"],
                ["payment", "Payment & remit details"]].map(([id, label]) => (
                <button key={id} onClick={() => setTab(id)}
                  className={`text-xs font-bold px-3 py-2 border-b-2 ${tab === id
                    ? "border-sky-700 text-sky-800" : "border-transparent text-slate-500"}`}
                  data-testid={`ledger-tab-${id}`}>{label}</button>
              ))}
            </div>

            {tab === "entry" && (
              <div className="mt-3" data-testid="ledger-entry-tab">
                <SupplierPayablesDesk
                  token={token}
                  supplierId={supplierId}
                  supplierName={supplierName}
                  onChanged={load}
                  embedded
                />
              </div>
            )}

            {tab === "invoices" && (
              <table className="w-full min-w-[720px] text-sm mt-2">
                <thead className="bg-slate-50"><tr>
                  {["Date", "Invoice #", "Our PO", "Due", "Status", "Billed", "Paid", "Balance"].map((h) => (
                    <th key={h} className="text-left px-2 py-1.5 text-[9.5px] font-black uppercase tracking-wider text-slate-500">{h}</th>
                  ))}
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {(data.invoices || []).map((r) => (
                    <tr key={r.id} className={r.overdue ? "bg-red-50/50" : ""} data-testid={`ledger-invoice-${r.id}`}>
                      <td className="px-2 py-1.5 text-[11px]">{r.invoice_date || "—"}</td>
                      <td className="px-2 py-1.5 text-[11px] font-mono font-bold">{r.invoice_number}</td>
                      <td className="px-2 py-1.5 text-[11px]">{r.po_number || "—"}</td>
                      <td className="px-2 py-1.5 text-[11px]">{r.due_date || "—"}
                        {r.overdue && <span className="block text-red-700 font-bold">{r.days_past_due}d late</span>}</td>
                      <td className="px-2 py-1.5 text-[11px]">{(r.status || "").replace(/_/g, " ")}
                        {!r.approved && r.balance > 0 && <span className="block text-amber-700 font-bold">unapproved</span>}</td>
                      <td className="px-2 py-1.5 text-[11px]">{money(r.amount)}</td>
                      <td className="px-2 py-1.5 text-[11px]">{money(r.paid_total)}</td>
                      <td className="px-2 py-1.5 text-[11px] font-black">{money(r.balance)}</td>
                    </tr>
                  ))}
                  {!(data.invoices || []).length && (
                    <tr><td colSpan={8} className="px-2 py-4 text-slate-400 text-sm">No invoices on file for this supplier.</td></tr>
                  )}
                </tbody>
              </table>
            )}

            {tab === "payments" && (
              <table className="w-full text-sm mt-2">
                <thead className="bg-slate-50"><tr>
                  {["Date", "Method", "Reference", "Bank", "Invoices", "Amount"].map((h) => (
                    <th key={h} className="text-left px-2 py-1.5 text-[9.5px] font-black uppercase tracking-wider text-slate-500">{h}</th>
                  ))}
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {(data.payments || []).map((r) => (
                    <tr key={r.id}>
                      <td className="px-2 py-1.5 text-[11px]">{(r.payment_date || "").slice(0, 10)}</td>
                      <td className="px-2 py-1.5 text-[11px] uppercase">{r.method}</td>
                      <td className="px-2 py-1.5 text-[11px] font-mono">{r.reference || "—"}</td>
                      <td className="px-2 py-1.5 text-[11px]">{r.bank_name || "—"}</td>
                      <td className="px-2 py-1.5 text-[11px]">{r.allocation_count}</td>
                      <td className="px-2 py-1.5 text-[11px] font-black">{money(r.amount)}</td>
                    </tr>
                  ))}
                  {!(data.payments || []).length && (
                    <tr><td colSpan={6} className="px-2 py-4 text-slate-400 text-sm">No payments recorded yet.</td></tr>
                  )}
                </tbody>
              </table>
            )}

            {tab === "pos" && (
              <table className="w-full text-sm mt-2">
                <thead className="bg-slate-50"><tr>
                  {["PO #", "Issued", "Status", "Amount"].map((h) => (
                    <th key={h} className="text-left px-2 py-1.5 text-[9.5px] font-black uppercase tracking-wider text-slate-500">{h}</th>
                  ))}
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {(data.purchase_orders || []).map((r) => (
                    <tr key={r.id}>
                      <td className="px-2 py-1.5 text-[11px] font-mono font-bold">{r.po_number}</td>
                      <td className="px-2 py-1.5 text-[11px]">{(r.issued_date || "").slice(0, 10) || "—"}</td>
                      <td className="px-2 py-1.5 text-[11px]">{r.status}</td>
                      <td className="px-2 py-1.5 text-[11px] font-black">{money(r.amount)}</td>
                    </tr>
                  ))}
                  {!(data.purchase_orders || []).length && (
                    <tr><td colSpan={4} className="px-2 py-4 text-slate-400 text-sm">No purchase orders issued to this supplier.</td></tr>
                  )}
                </tbody>
              </table>
            )}

            {tab === "payment" && (
              <div className="mt-3" data-testid="supplier-payment-profile">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {PROFILE_FIELDS.map(([k, label, ph]) => (
                    <label key={k} className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      {label}
                      <Input value={profile[k] ?? ""} placeholder={ph}
                        onChange={(e) => setProfile({ ...profile, [k]: e.target.value })}
                        className="h-8 text-xs mt-0.5 font-normal normal-case"
                        data-testid={`profile-${k}`} />
                    </label>
                  ))}
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    Tax ID / EIN {p.tax_id_last4 ? `(on file ••${p.tax_id_last4})` : ""}
                    <Input value={profile.tax_id ?? ""} placeholder="Enter to replace"
                      onChange={(e) => setProfile({ ...profile, tax_id: e.target.value })}
                      className="h-8 text-xs mt-0.5 font-normal normal-case" data-testid="profile-tax-id" />
                  </label>
                  <label className="flex items-center gap-2 text-[11px] text-slate-700 mt-4">
                    <input type="checkbox" checked={!!profile.w9_on_file}
                      onChange={(e) => setProfile({ ...profile, w9_on_file: e.target.checked })}
                      data-testid="profile-w9" />
                    W-9 on file
                  </label>
                </div>

                <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mt-4 inline-flex items-center gap-1.5">
                  <Banknote className="w-3.5 h-3.5" /> Bank & payment instructions
                  {p.verified_at && (
                    <span className="text-emerald-700 inline-flex items-center gap-1 normal-case tracking-normal font-bold">
                      <ShieldCheck className="w-3.5 h-3.5" /> verified {p.verified_at.slice(0, 10)} by {p.verified_by}
                    </span>
                  )}
                </p>
                <p className="text-[10.5px] text-slate-500 mb-2">
                  Account and routing numbers are masked. Revealing them is limited to admins and
                  written to the audit log. Never change these off an emailed request — call a known contact.
                </p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {BANK_FIELDS.map(([k, label]) => (
                    <label key={k} className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      {label}
                      <Input
                        value={bank[k] ?? (revealed ? (revealed[k] || "") : (onFile[k] || ""))}
                        onChange={(e) => setBank({ ...bank, [k]: e.target.value })}
                        className="h-8 text-xs mt-0.5 font-mono font-normal normal-case"
                        data-testid={`bank-${k}`} />
                    </label>
                  ))}
                </div>

                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={reveal} data-testid="bank-reveal">
                    <Eye className="w-3.5 h-3.5 mr-1" /> Reveal full details
                  </Button>
                  <Button size="sm" onClick={() => save(false)} disabled={saving}
                    className="bg-slate-900 hover:bg-slate-800" data-testid="profile-save">
                    {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                    Save details
                  </Button>
                  <Button size="sm" onClick={() => save(true)} disabled={saving}
                    className="bg-emerald-600 hover:bg-emerald-700" data-testid="profile-verify">
                    <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Save & mark verified by phone
                  </Button>
                </div>

                {!!(p.change_history || []).length && (
                  <div className="mt-4">
                    <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">
                      Change history
                    </p>
                    {p.change_history.slice().reverse().map((h, i) => (
                      <p key={i} className="text-[11px] text-slate-600">
                        {h.at ? new Date(h.at).toLocaleString() : ""} · {h.by} · {h.summary}
                        {h.source ? ` (${h.source})` : ""}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierLedgerDialog;
